/** The create wizard's "Create a sandbox too" (SBX D8; metaplan S5b): a checkbox on the last step, and —
 *  when it is ticked — the sandbox's admin, outbound mode and tester allowlist. The backend creates
 *  production and `<code>sbx` in one transaction (a failure rolls both back) and answers with both.
 *
 *  Part of the wizard's own local state (`WizardData`, like every other create step): the answers are
 *  sent only with the box ticked (`createPayload`). "Use production's vendor key" is not offered here —
 *  a new operator has no key yet; "Add sandbox" on an existing operator offers it while the gate is open. */
import { Box, Checkbox, FormControlLabel, Paper, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { sandboxApi, sandboxKeys } from '@/api/sandbox';

import type { WizardData } from '../wizard/types';
import { AllowlistEditor } from './AllowlistEditor';
import { OutboundModeRadios } from './OutboundSection';

/** "Production: acme · Sandbox: acmesbx" — what one click creates. */
export function PairedSummary({ code, withSandbox }: { code: string; withSandbox: boolean }) {
  const c = code.trim() || '<code>';
  return (
    <Typography variant="body2" color="text.secondary" data-paired-summary>
      Production:{' '}
      <Box component="strong" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
        {c}
      </Box>
      {withSandbox && (
        <>
          {' · '}Sandbox:{' '}
          <Box
            component="strong"
            sx={{ fontFamily: 'monospace', color: 'text.primary' }}
          >{`${c}sbx`}</Box>
        </>
      )}
    </Typography>
  );
}

export function WizardSandboxSection({
  data,
  onChange,
}: {
  data: WizardData;
  onChange: (partial: Partial<WizardData>) => void;
}) {
  const options = useQuery({
    queryKey: sandboxKeys.options,
    queryFn: sandboxApi.options,
    enabled: data.create_sandbox,
  });
  const code = data.code.trim() || '<code>';
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }} component="section" aria-label="Sandbox">
      <FormControlLabel
        control={
          <Checkbox
            checked={data.create_sandbox}
            onChange={(e) => onChange({ create_sandbox: e.target.checked })}
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Create a sandbox too —{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{`${code}sbx`}</Box>
          </Typography>
        }
      />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, maxWidth: '75ch' }}>
        A tenant of its own for building and testing the integration: test credentials only, and
        everything it sends stays in its outbox unless the destination is allowlisted. It can also
        be added later, from the operator’s Sandbox tab.
      </Typography>
      {data.create_sandbox && (
        <Box sx={{ display: 'grid', gap: 2.5, mt: 2, ml: { sm: 4 } }}>
          <PairedSummary code={data.code} withSandbox />
          <TextField
            size="small"
            label="Sandbox admin e-mail"
            value={data.sandbox_admin_email}
            onChange={(e) => onChange({ sandbox_admin_email: e.target.value })}
            placeholder={data.admin_email || 'The admin e-mail'}
            helperText="A separate account from production’s, with the same password. Blank: the production admin’s e-mail."
            sx={{ maxWidth: 480 }}
          />
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1.3fr)' },
            }}
          >
            <Box>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
                Outbound mode
              </Typography>
              <OutboundModeRadios
                value={data.sandbox_outbound_mode}
                onChange={(mode) => onChange({ sandbox_outbound_mode: mode })}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                Allowlist
              </Typography>
              <AllowlistEditor
                value={data.sandbox_allowlist}
                onChange={(allowlist) => onChange({ sandbox_allowlist: allowlist })}
                ownDomains={options.data?.own_domains ?? []}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
